import React, { useState, useRef, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { advisorAPI, projectsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import { Bot, Send, Sparkles, Leaf, Lightbulb, User, Building2 } from 'lucide-react';
import { toast } from 'sonner';

const AIAdvisor = () => {
  const [searchParams] = useSearchParams();
  const projectIdFromUrl = searchParams.get('projectId');

  const [projects, setProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState(projectIdFromUrl || 'none');
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [projectsLoading, setProjectsLoading] = useState(true);
  const scrollRef = useRef(null);

  const suggestedQuestions = [
    "How can I reduce embodied carbon in my design?",
    "What are the best low-carbon alternatives to concrete?",
    "What BREEAM credits relate to embodied carbon?",
    "How does timber compare to steel for structural frames?",
    "What's the typical carbon footprint for a UK office building?",
  ];

  useEffect(() => {
    fetchProjects();
  }, []);

  useEffect(() => {
    // Scroll to bottom when messages change
    if (scrollRef.current) {
      scrollRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  const fetchProjects = async () => {
    try {
      const response = await projectsAPI.getAll();
      setProjects(response.data);
    } catch (error) {
      console.error('Failed to load projects');
    } finally {
      setProjectsLoading(false);
    }
  };

  const handleSendMessage = async (message = inputMessage) => {
    if (!message.trim()) return;

    const userMessage = {
      role: 'user',
      content: message,
      timestamp: new Date().toISOString(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputMessage('');
    setLoading(true);

    try {
      const selectedProject = projects.find((p) => p.id === selectedProjectId && selectedProjectId !== 'none');
      const context = selectedProject
        ? `Project: ${selectedProject.name}, Type: ${selectedProject.building_type || 'N/A'}, Area: ${selectedProject.gross_floor_area || 'N/A'} m²`
        : null;

      const response = await advisorAPI.ask(
        message,
        selectedProjectId === 'none' ? null : selectedProjectId,
        context
      );

      const aiMessage = {
        role: 'assistant',
        content: response.data.response,
        suggestions: response.data.suggestions,
        timestamp: new Date().toISOString(),
      };

      setMessages((prev) => [...prev, aiMessage]);
    } catch (error) {
      toast.error('Failed to get AI response');
      const errorMessage = {
        role: 'assistant',
        content: "I'm sorry, I encountered an error processing your request. Please try again.",
        timestamp: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleSuggestionClick = (question) => {
    handleSendMessage(question);
  };

  return (
    <div className="p-8 h-[calc(100vh-65px)] flex flex-col" data-testid="ai-advisor-page">
      {/* Header */}
      <div className="page-header -mx-8 -mt-8 mb-6 px-8 py-6 bg-white border-b border-slate-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#0F2818] to-[#2D5A3F] flex items-center justify-center">
              <Bot className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                AI Sustainability Advisor
              </h1>
              <p className="text-slate-600 mt-0.5">
                Get expert guidance on reducing embodied carbon
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Select value={selectedProjectId} onValueChange={setSelectedProjectId}>
              <SelectTrigger className="w-64" data-testid="project-context-select">
                <div className="flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-slate-500" />
                  <SelectValue placeholder="Select project for context" />
                </div>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No project context</SelectItem>
                {projects.map((project) => (
                  <SelectItem key={project.id} value={project.id}>
                    {project.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Chat Container */}
      <div className="flex-1 flex gap-6 min-h-0">
        {/* Main Chat */}
        <Card className="flex-1 flex flex-col border-slate-200 overflow-hidden" data-testid="chat-container">
          <ScrollArea className="flex-1 p-6">
            <div className="space-y-6">
              {/* Welcome message if no messages */}
              {messages.length === 0 && (
                <div className="text-center py-12" data-testid="welcome-message">
                  <div className="w-16 h-16 rounded-2xl bg-[#E8EEEA] flex items-center justify-center mx-auto mb-4">
                    <Sparkles className="w-8 h-8 text-[#0F2818]" />
                  </div>
                  <h2 className="text-xl font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Hello! I'm your Sustainability Advisor
                  </h2>
                  <p className="text-slate-600 max-w-md mx-auto mb-8">
                    I can help you reduce embodied carbon, select sustainable materials, 
                    and navigate BREEAM/LEED certification requirements.
                  </p>

                  {/* Suggested Questions */}
                  <div className="max-w-2xl mx-auto">
                    <p className="text-sm text-slate-500 mb-3">Try asking:</p>
                    <div className="flex flex-wrap gap-2 justify-center">
                      {suggestedQuestions.map((question, index) => (
                        <button
                          key={index}
                          onClick={() => handleSuggestionClick(question)}
                          className="px-4 py-2 bg-white border border-slate-200 rounded-full text-sm text-slate-700 hover:bg-slate-50 hover:border-slate-300 transition-colors"
                          data-testid={`suggested-question-${index}`}
                        >
                          {question}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Messages */}
              {messages.map((message, index) => (
                <div
                  key={index}
                  className={`flex gap-4 ${message.role === 'user' ? 'justify-end' : ''}`}
                  data-testid={`message-${index}`}
                >
                  {message.role === 'assistant' && (
                    <div className="w-10 h-10 rounded-xl bg-[#E8EEEA] flex items-center justify-center flex-shrink-0">
                      <Bot className="w-5 h-5 text-[#0F2818]" />
                    </div>
                  )}
                  <div
                    className={`max-w-[70%] ${
                      message.role === 'user'
                        ? 'bg-[#0F2818] text-white rounded-2xl rounded-tr-md px-5 py-3'
                        : 'bg-[#E8EEEA] text-slate-800 rounded-2xl rounded-tl-md px-5 py-4'
                    }`}
                  >
                    <div className="whitespace-pre-wrap text-sm leading-relaxed">
                      {message.content}
                    </div>
                    
                    {/* Suggestions from AI */}
                    {message.role === 'assistant' && message.suggestions?.length > 0 && (
                      <div className="mt-4 pt-4 border-t border-[#0F2818]/10">
                        <p className="text-xs text-slate-500 mb-2 flex items-center gap-1">
                          <Lightbulb className="w-3 h-3" />
                          Key takeaways:
                        </p>
                        <ul className="space-y-1">
                          {message.suggestions.slice(0, 3).map((suggestion, idx) => (
                            <li key={idx} className="text-xs text-slate-600 flex items-start gap-2">
                              <span className="w-1.5 h-1.5 rounded-full bg-[#0F2818] mt-1.5 flex-shrink-0" />
                              {suggestion}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                  {message.role === 'user' && (
                    <div className="w-10 h-10 rounded-xl bg-slate-200 flex items-center justify-center flex-shrink-0">
                      <User className="w-5 h-5 text-slate-600" />
                    </div>
                  )}
                </div>
              ))}

              {/* Loading indicator */}
              {loading && (
                <div className="flex gap-4" data-testid="loading-indicator">
                  <div className="w-10 h-10 rounded-xl bg-[#E8EEEA] flex items-center justify-center flex-shrink-0">
                    <Bot className="w-5 h-5 text-[#0F2818]" />
                  </div>
                  <div className="bg-[#E8EEEA] rounded-2xl rounded-tl-md px-5 py-4">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-[#0F2818] rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                      <div className="w-2 h-2 bg-[#0F2818] rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                      <div className="w-2 h-2 bg-[#0F2818] rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                  </div>
                </div>
              )}

              <div ref={scrollRef} />
            </div>
          </ScrollArea>

          {/* Input Area */}
          <div className="p-4 border-t border-slate-200 bg-white">
            <div className="flex gap-3">
              <Input
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Ask about sustainable materials, carbon reduction, BREEAM..."
                className="flex-1"
                disabled={loading}
                data-testid="chat-input"
              />
              <Button
                onClick={() => handleSendMessage()}
                disabled={loading || !inputMessage.trim()}
                className="bg-[#0F2818] hover:bg-[#06140C]"
                data-testid="send-button"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </Card>

        {/* Sidebar */}
        <div className="w-80 space-y-6 hidden lg:block">
          {/* Quick Tips */}
          <Card className="border-slate-200" data-testid="quick-tips">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <Lightbulb className="w-4 h-4 text-[#800020]" />
                Quick Tips
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-xs text-slate-600 leading-relaxed">
                  <span className="font-semibold text-slate-900">Timber vs Steel:</span> Softwood timber has ~0.31 kgCO2e/kg compared to steel's 1.46 kgCO2e/kg
                </p>
              </div>
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-xs text-slate-600 leading-relaxed">
                  <span className="font-semibold text-slate-900">Recycled Materials:</span> Using recycled steel can reduce embodied carbon by up to 68%
                </p>
              </div>
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-xs text-slate-600 leading-relaxed">
                  <span className="font-semibold text-slate-900">BREEAM Mat 01:</span> Up to 6 credits available for life cycle assessment
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Certifications */}
          <Card className="border-slate-200" data-testid="certifications-info">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <Leaf className="w-4 h-4 text-[#0F2818]" />
                Supported Certifications
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                  BREEAM
                </Badge>
                <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                  LEED
                </Badge>
                <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">
                  WELL
                </Badge>
                <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                  Passivhaus
                </Badge>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AIAdvisor;
